import React from 'react';
import { useHistory, useLocation } from 'react-router';

import { RematchingWhyPage } from '../steps/1-why.page';
import { RematchingMoreDetailsPage } from '../steps/2-more-details.page';
import { RematchingWhatPage } from '../steps/3-what.page';
import { MatchingData, RematchingProvidersPage } from '../steps/4-providers.page';
import { RematchingConfirmPage } from '../steps/5-confirm.page';
import { LightLayout } from '../../../components';
import { useRematchingMachine } from './machine';
import { Loading } from '@uplift/pages';
import { RematchingNewAddressPage } from '../steps/2-new-address.page';
import { Button } from '@uplift/ui-v2';

export type RematchingStateProps = {
  stateMachine: ReturnType<typeof useRematchingMachine>;
  onBack: () => void;
};

export const RematchingState: React.VFC<RematchingStateProps> = ({ stateMachine, onBack }) => {
  const history = useHistory();
  const location = useLocation<{referrer?: string}>();
  const onExit = () => history.push(location.state?.referrer || '/');
  const [ state, send ] = stateMachine;
  const exitButton = <Button color="secondary" onClick={onExit}>Cancel Rematch</Button>;

  const matchingData: MatchingData = {
    ...state.context,
    stateId: state.context.newAddress ? state.context.newAddress.state : state.context.stateId,
  };

  if (state.value === 'WHY') {
    return (
      <LightLayout leftActions={exitButton}>
        <RematchingWhyPage
          rematchReasons={state.context.rematchReasons}
          onBack={onBack}
          onNext={(rematchReasons) => send({ type: 'WHY.NEXT', data: { rematchReasons } })}
        />
      </LightLayout>
    );
  }

  if (state.value === 'MORE_DETAILS') {
    return (
      <LightLayout leftActions={exitButton}>
        <RematchingMoreDetailsPage
          details={state.context.details}
          back={onBack}
          next={(details) => send({ type: 'MORE_DETAILS.NEXT', data: { details } })}
        />
      </LightLayout>
    );
  }

  if (state.value === 'NEW_ADDRESS') {
    return (
      <LightLayout leftActions={exitButton}>
        <RematchingNewAddressPage
          onBack={onBack}
          onNext={(data) => send({ type: 'NEW_ADDRESS.NEXT', data })}
          newAddress={state.context.newAddress}
        />
      </LightLayout>
    );
  }

  if (state.value === 'WHAT') {
    return (
      <LightLayout leftActions={exitButton}>
        <RematchingWhatPage
          onBack={onBack}
          onNext={(data) => send({ type: 'WHAT.NEXT', data })}
          specialtyIds={state.context.specialtyIds}
        />
      </LightLayout>
    );
  }

  if (state.value === 'PROVIDERS') {
    return (
      <RematchingProvidersPage
        exitAction={exitButton}
        onBack={onBack}
        onNext={(data) => send({ type: 'PROVIDERS.NEXT', data })}
        hasNoTherapist={state.context.hasNoTherapist ?? false}
        matchProviderId={state.context.matchProviderId}
        onUpdateFilters={(data) => send({ type: 'PROVIDERS.UPDATE_FILTERS', data })}

        matchingData={matchingData}
      />
    );
  }

  if (state.value === 'CONFIRM') {
    return (
      <LightLayout leftActions={exitButton}>
        <RematchingConfirmPage
          newAddress={state.context.newAddress ?? null}
          rematchReasons={state.context.rematchReasons}
          oldMatchId={state.context.matchId}
          oldProviderId={state.context.matchProviderId}
          newProvider={state.context.newProvider}
          selectedSlot={state.context.selectedSlot}
          details={state.context.details}
          onConfirm={() => history.push('/')}
          onReject={() => history.push('/')}
          matchingData={matchingData}
        />
      </LightLayout>
    );
  }

  return (
    <LightLayout leftActions={exitButton}>
      <Loading />
    </LightLayout>
  );
};
